import { includes } from '@hover/utilities';
import type { TFunction } from 'i18next';

import type { AiImage } from 'src/modules/homeownerApp/types';
import { isAiGenerated } from 'src/modules/homeownerApp/types';
import type { AiImageEdits } from 'src/shared/graphql/generated';
import { TranslationNamespace, useTranslate } from 'src/shared/hooks';

import type { Region } from '../../constants/customizationCategories';

export type RegionWithDisabledRegions = Region | 'fence';

export const SurfaceClassToRegion: Record<
  string,
  RegionWithDisabledRegions | undefined
> = {
  Door: 'door',
  Wall: 'walls',
  Roof: 'roof',
  Window: 'windows',
  Shutter: 'shutter',
  GarageDoor: 'garage_door',
  Soffit: 'trim',
  Fascia: 'trim',
  WindowTrim: 'trim',
  DoorTrim: 'trim',
  OtherTrim: 'trim',
};

// This is effectively camelcased RegionWithDisabledRegions
type AiImageEditsKeysWithoutMetadata = Omit<
  AiImageEdits,
  'schemaVersion' | '__typename'
>;

export const mapRegionToDisplayText = (
  t: TFunction,
  region: RegionWithDisabledRegions,
) => t(`regions.${region}`);

export const getSnakecaseRegionFromKey = (
  camelCaseRegion: keyof AiImageEdits,
): RegionWithDisabledRegions | undefined => {
  // TODO: handle `instances` edit model
  if (includes(['__typename', 'schemaVersion', 'instances'], camelCaseRegion)) {
    return undefined;
  }
  let region: RegionWithDisabledRegions;
  switch (camelCaseRegion) {
    case 'garageDoor':
      region = 'garage_door';
      break;
    default:
      region = camelCaseRegion as RegionWithDisabledRegions;
      break;
  }

  return region;
};

export const useHumanReadableEditDescription = (image: AiImage) => {
  const t = useTranslate(TranslationNamespace.HOMEOWNER_APP);
  const editsDescription: Array<string> = [];

  const isCustomized =
    isAiGenerated(image) &&
    !!image.metadata?.allEdits &&
    !!Object.keys(image.metadata.allEdits).length;

  if (!isCustomized || !image.metadata?.allEdits) {
    return [];
  }

  const { allEdits } = image.metadata;

  Object.keys(allEdits).forEach(key => {
    const region = getSnakecaseRegionFromKey(key as keyof AiImageEdits);

    if (!region) return;

    const regionDisplayName = mapRegionToDisplayText(t, region);
    const edit = allEdits[key as keyof AiImageEditsKeysWithoutMetadata] || {};

    // TODO: handle `instances` edit model
    if (Array.isArray(edit)) return;

    if ('paint' in edit && edit?.paint) {
      editsDescription.push(
        t('fullscreenImage.paintedRegionDescription', {
          region: regionDisplayName,
          name: edit.paint.name,
          manufacturer: edit.paint.manufacturer,
        }),
      );
    }

    if ('material' in edit && edit?.material && edit.material.palette) {
      editsDescription.push(
        t('fullscreenImage.materialRegionDescription', {
          category: edit.material.palette,
          name: edit.material.name,
          region: regionDisplayName,
        }),
      );
    }
  });

  return editsDescription;
};
